// Catalog.ID Client — Account Blobs in the Offline Bundle
// ============================================================================
// Two pure functions either side of the bundle's `blobs` section
// (cid-encrypted-storage §4.2): what to put in one, and what to put back.
//
// WHY the bundle carries blobs at all. The key bag survives the loss of every
// node because the member holds their own copies (Recovery Kit Addendum §5).
// The blob store would not: one generation is retained, no device mirrors it,
// and the `ls-backup` spill that reaches the bag deliberately never reaches a
// blob. Without this section a member with a perfect recovery kit would come
// back with their identity intact and their application state gone.
//
// WHY the restore never overwrites. A row the server already holds is NEWER
// than the bundle's by definition: the bundle is a snapshot of one moment, the
// server row is whatever the account has done since. Restoring over it would
// silently roll a member's wallet list or vault back to the day they exported
// a backup. So the plan is strictly fill-in-the-gaps.
//
// DOM-free and network-free: the API is injected, so both halves are unit
// tested without a session.

import type { BundleBlob } from './bundle';
import type { BlobManifestEntry, BlobRecord, PutBlobBody, PutBlobResult } from '../api/types';

/** The two reads a collection needs. Matches `api/blobs.ts`. */
export interface BundleBlobSource {
    listBlobs(): Promise<{ blobs: BlobManifestEntry[] }>;
    getBlob(moduleId: string, name: string): Promise<BlobRecord>;
}

/**
 * Every blob of the account, ready for `ExportBundleParams.blobs`. Spans EVERY
 * namespace, not only the vault's own: a bundle is the account's backup, and a
 * module's document is as unrecoverable as the vault's.
 *
 * A manifest row whose key is not exactly `<moduleId>/<name>` is skipped. The
 * server only writes two-segment keys, so such a row would be a bug elsewhere,
 * and guessing at its shape would put a blob in the bundle under a key nothing
 * could restore.
 */
export async function collectBlobsForBundle(api: BundleBlobSource): Promise<BundleBlob[]> {
    const { blobs } = await api.listBlobs();
    const collected: BundleBlob[] = [];
    for (const entry of blobs) {
        const segments = entry.blobKey.split('/');
        if (segments.length !== 2 || !segments[0] || !segments[1]) continue;
        const row = await api.getBlob(segments[0], segments[1]);
        collected.push({
            blob_key: entry.blobKey,
            generation: row.generation,
            nonce: row.nonce,
            ciphertext: row.ciphertext,
        });
    }
    return collected;
}

/**
 * The blobs a restore should push: those the server has no row for at all.
 *
 * @param bundleBlobs    the bundle's section
 * @param serverManifest `GET /api/account/blobs` as it stands right now
 */
export function planBlobRestore(
    bundleBlobs: BundleBlob[],
    serverManifest: { blobKey: string }[],
): BundleBlob[] {
    const onServer = new Set(serverManifest.map(m => m.blobKey));
    return bundleBlobs.filter(b => !onServer.has(b.blob_key));
}

/** The two calls a restore needs. Matches `api/blobs.ts`. */
export interface BundleBlobSink {
    listBlobs(): Promise<{ blobs: BlobManifestEntry[] }>;
    putBlob(
        moduleId: string, name: string, body: PutBlobBody, ifGeneration: number,
    ): Promise<PutBlobResult>;
}

/**
 * Push the bundle's blobs that the server is missing, each as a CREATE
 * (`ifGeneration: 0`). Best-effort by construction: this runs on the unlock
 * path, and an unlock must never fail over application state. A blob that
 * cannot be restored is counted, not thrown — the member is logged in either
 * way, and the module that owns the document will notice it is missing.
 *
 * `ifGeneration: 0` is also the safety belt: if a row appeared between the
 * manifest read and the write, the server refuses with 409 and the newer row
 * stands.
 *
 * @returns how many were restored and how many were attempted and failed.
 */
export async function restoreBlobsFromBundle(
    bundleBlobs: BundleBlob[],
    api: BundleBlobSink,
): Promise<{ restored: number; failed: number }> {
    let restored = 0;
    let failed = 0;
    const { blobs } = await api.listBlobs();
    for (const blob of planBlobRestore(bundleBlobs, blobs)) {
        const segments = blob.blob_key.split('/');
        if (segments.length !== 2 || !segments[0] || !segments[1]) {
            failed++;
            continue;
        }
        try {
            await api.putBlob(
                segments[0], segments[1],
                { ciphertext: blob.ciphertext, nonce: blob.nonce },
                0);
            restored++;
        } catch (err) {
            console.warn(`[bundle] could not restore blob '${blob.blob_key}':`, err);
            failed++;
        }
    }
    return { restored, failed };
}
